import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, ShipmentPackageDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SmallHeadingAndTextOrderLineDelivery from "@WhiteCap/Components/SmallHeadingAndTextOrderLineDelivery";
import StatusLableAndHeading from "@WhiteCap/Components/StatusLableAndHeading";
import StatusLableAndHeadingOrderDetail from "@WhiteCap/Components/StatusLableAndHeadingOrderDetail";
import { OrderLinesListStyles } from "@WhiteCap/Overrides/Components/OrderLinesList";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { ApiResultModel as OsnOrderDataModel } from "@WhiteCap/Store/Service/CustomOrderStatusPageService";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;
    return {
        isLoading: state.WhiteCap.podDataStore.isLoading,
        language: state.context.session.language,
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
        descartesDeliveryMethod: state.context.settings.properties?.descartesDeliveryMethod,
        publicHolidays: state.context.settings.properties?.publicHolidays,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderDataRealtime {
    realtimeOrderStatusdeliveryId?: RealtimeOrderStatusdeliveryID;
    shipmentDate?: Date;
    orderLine: OrderLineModel[];
    carrier?: string;
}

export interface RealtimeOrderStatusdeliveryID {
    status?: string;
    eta?: Date;
    arrivalTime?: Date;
    driverName?: string;
    deliveryId?: string;
    podId?: string;
    trackingURL?: string;
    signedBy?: string;
    shipVia?: string;
    trackingNumber?: string;
    stopsAway?: string;
}

export interface OrderLineNotesStyles {
    /**
     * @deprecated Use orderLines.orderLineNotes.typographyStyles instead.
     */
    typographyStyles?: TypographyProps;
    /**
     * @deprecated Use orderLines.orderLineNotes.gridItemStyles instead.
     */
    gridItemStyles?: GridItemProps;
}

export interface OrderDetailsInvoiceTableStyles {
    orderLines?: OrderLinesListStyles;
    gridContainer?: GridContainerProps;
    dataTable?: DataTableProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    mobileHeaderFirstrow?: InjectableCss;
    mobileHeaderSecondtrow?: InjectableCss;
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
    invoiceNumberHeadingText?: SmallHeadingAndTextStyles;
    statusHeadingText?: SmallHeadingAndTextStyles;
    wrapper?: InjectableCss;
}

export const invoiceTableStyles: OrderDetailsInvoiceTableStyles = {
    orderLines: {
        gridContainer: {
            css: css`
                .card_container__wrapper {
                    border-top: none !important;
                    border-bottom: none;
                }
                .card_container__wrapper ~ .card_container__wrapper {
                    border-top: none;
                }
            `,
        },
    },
    mobileHeaderFirstrow: {
        css: css`
            width: auto;
            height: 100%;
            min-height: 55px;
            padding: 0 12px;
            display: flex;
            align-items: center;
            p {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 7px;
                    `,
                    css`
                        padding: 0 7px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    mobileHeaderSecondtrow: {
        css: css`
            width: auto;
            height: 100%;
            min-height: 55px;
            display: flex;
            padding: 0 12px;

            align-items: center;
            p {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        p {
                            text-align: center;
                        }
                    `,
                    css`
                        p {
                            text-align: center;
                        }
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    orderNumberHeadingAndText: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 500;
                font-size: 12px !important;
                min-width: max-content;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 12px !important;
                font-weight: 350;
                min-width: max-content;
            `,
        },
    },
    statusHeadingText: {
        heading: {
            css: css`
                display: none;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 12px !important;
                font-weight: 350;
            `,
        },
    },
    invoiceNumberHeadingText: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 350 !important;
                font-size: 12px !important;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 12px !important;
                font-weight: 500 !important;
            `,
        },
    },
    dataTableRow: {
        css: css`
            display: flex;
            padding: 5px 16px;
        `,
    },
    dataTableCell: {
        css: css`
            height: 40px !important;
            border: none !important;
            align-content: center;
        `,
    },
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding-bottom: 6px;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css``,
                ])}
        `,
    },
};

const styles = invoiceTableStyles;

const OrderDetailsInvoiceTable: React.FunctionComponent<Props> = ({ orderData, pageType }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;
    if (!order || !order.orderLines) {
        return null;
    }

    const shipmentStatusByDeliveryId = order?.properties.shipmentStatus
        ? JSON.parse(order.properties.shipmentStatus)
        : {};

    const groupByDeliveryId = (orderLines: OrderLineModel[], shipmentPackages?: ShipmentPackageDto[] | null) => {
        const shipmentPackagesMap: { [packageNumber: string]: ShipmentPackageDto } = {};

        if (shipmentPackages) {
            shipmentPackages.forEach(shipmentPackage => {
                shipmentPackagesMap[shipmentPackage.packageNumber.split("-")[0]] = shipmentPackage;
            });
        }

        return orderLines.reduce<{ [deliveryId: string]: OrderDataRealtime }>((grouped, orderLine) => {
            let deliveryIds: string[] = [];
            if (orderLine.properties?.deliveryId?.includes(",")) {
                deliveryIds = orderLine.properties.deliveryId.split(",").map(id => id.trim());
            } else {
                deliveryIds.push(orderLine.properties?.deliveryId?.trim());
            }

            deliveryIds.forEach(deliveryId => {
                // Skip null or undefined deliveryIds
                if (!deliveryId) {
                    return;
                }
                if (!grouped[deliveryId]) {
                    grouped[deliveryId] = {
                        orderLine: [],
                    };
                }
                grouped[deliveryId].shipmentDate = shipmentPackagesMap[deliveryId]?.shipmentDate;
                grouped[deliveryId].orderLine.push({ ...orderLine });
                grouped[deliveryId].carrier =
                    shipmentPackagesMap[deliveryId]?.carrier || order?.shipViaDescription || order?.shipCode;

                const shipmentPackage = shipmentPackagesMap[deliveryId];
                if (shipmentPackage?.shipmentPackageLineDtos) {
                    shipmentPackage.shipmentPackageLineDtos.forEach(shipmentPackageLine => {
                        let matchedOrderLine = grouped[deliveryId].orderLine.find(
                            orderLine => orderLine.id === shipmentPackageLine.orderLineId,
                        );
                        if (matchedOrderLine) {
                            matchedOrderLine.qtyOrdered = shipmentPackageLine.qtyOrdered;
                        }

                        if (!matchedOrderLine) {
                            matchedOrderLine = grouped[deliveryId].orderLine.find(
                                orderLine => orderLine.productErpNumber === shipmentPackageLine.productName,
                            );
                            if (matchedOrderLine) {
                                matchedOrderLine.qtyOrdered = shipmentPackageLine.qtyShipped;
                                matchedOrderLine.lineTotal = shipmentPackageLine.price;
                                matchedOrderLine.lineTotalDisplay = `$${shipmentPackageLine.price}`;
                                matchedOrderLine.extendedUnitNetPrice = shipmentPackageLine.price;
                                matchedOrderLine.extendedUnitNetPriceDisplay = `$${shipmentPackageLine.price}`;
                            }
                        }
                    });
                }
            });

            return grouped;
        }, {});
    };

    const groupedOrderLines = groupByDeliveryId(order.orderLines, order?.shipmentPackages);

    Object.entries(groupedOrderLines).forEach(([deliveryId, orderLines]) => {
        if (order.properties?.realtimeOrderStatus) {
            const realtimeOrderStatus = JSON.parse(order.properties?.realtimeOrderStatus);
            realtimeOrderStatus.forEach((realtimeOrderStatusdeliveryId: any) => {
                const matchedOrder = realtimeOrderStatusdeliveryId.deliveryId === deliveryId;
                if (matchedOrder) {
                    groupedOrderLines[deliveryId] = {
                        ...groupedOrderLines[deliveryId],
                        realtimeOrderStatusdeliveryId,
                    };
                }
            });
        } else if (order.shipmentPackages) {
            const realtimeOrderShipmentStatus = order.shipmentPackages;
            realtimeOrderShipmentStatus.forEach(realtimeOrderStatusdeliveryId => {
                groupedOrderLines[deliveryId] = {
                    ...groupedOrderLines[deliveryId],
                    realtimeOrderStatusdeliveryId: {
                        trackingURL: realtimeOrderStatusdeliveryId.trackingUrl,
                        shipVia: realtimeOrderStatusdeliveryId.shipVia,
                        trackingNumber: realtimeOrderStatusdeliveryId.trackingNumber,
                    },
                };
            });
        }
    });

    const getDeliveryMethod = (orderLines: OrderDataRealtime) => {
        return orderLines.orderLine[0].properties?.shippingMethod
            ? orderLines.orderLine[0].properties?.shippingMethod
            : order.shipViaDescription
            ? order.shipViaDescription
            : order.shipCode;
    };

    const getInvoiceValueForPackage = (packageNumber: string, deliveryInvoiceNumbers: string | undefined) => {
        if (!deliveryInvoiceNumbers) {
            return "";
        }

        const invoiceNumbers = deliveryInvoiceNumbers
            .split(",")
            .reduce((acc: { [key: string]: string }, item: string) => {
                const [key, value] = item.split(":").map(str => str.trim());
                acc[key] = value;
                return acc;
            }, {});
        const packageNumberPrefix = packageNumber.split("-")[0];
        return invoiceNumbers[packageNumberPrefix] || "";
    };

    const getOrderStatus = (orderLines: OrderDataRealtime, deliveryId?: string) => {
        return orderLines.realtimeOrderStatusdeliveryId?.status
            ? orderLines.realtimeOrderStatusdeliveryId?.status
            : deliveryId && shipmentStatusByDeliveryId[deliveryId]
            ? shipmentStatusByDeliveryId[deliveryId]
            : order.statusDisplay;
    };

    return (
        <StyledWrapper
            className="invoicehistory-wrapper"
            css={css`
                display: flex;
                flex-wrap: wrap;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            flex: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                        css`
                            > div:last-child {
                                width: 100%;
                            }

                            > div:nth-child(odd):not(:last-child),
                            > div:nth-child(even) {
                                width: 50%;
                            }
                        `,
                    ]);
                }}
            `}
        >
            {Object.entries(groupedOrderLines).map(([deliveryId, orderLines], index) => (
                <StyledWrapper key={index} {...styles.wrapper}>
                    <Hidden
                        below="md"
                        css={css`
                            width: 100%;
                            padding: 6px 6px 0px 0px;
                            td {
                                min-width: max-content;
                                height: 40px !important;
                                background-color: ${getColor("common.accent")};
                            }
                        `}
                    >
                        <DataTable {...styles.dataTable}>
                            <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                                <DataTableRow {...styles.dataTableRow}>
                                    <DataTableCell {...styles.dataTableCell}>
                                        {(orderLines.orderLine[0].properties?.shippingMethod ||
                                            order.shipViaDescription ||
                                            order.shipCode) && (
                                            <SmallHeadingAndTextOrderLineDelivery
                                                heading={`DELIVERY ${
                                                    Object.entries(groupedOrderLines).length > 1
                                                        ? `${index + 1}/${Object.entries(groupedOrderLines).length}`
                                                        : ""
                                                }`}
                                                text={orderLines.carrier || ""}
                                                extendedStyles={styles.orderNumberHeadingAndText}
                                            />
                                        )}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell}>
                                        {(orderLines.realtimeOrderStatusdeliveryId?.status ||
                                            shipmentStatusByDeliveryId[deliveryId] ||
                                            order.statusDisplay) && (
                                            <StatusLableAndHeadingOrderDetail
                                                heading={translate("Status")}
                                                text={getOrderStatus(orderLines, deliveryId)}
                                                extendedStyles={styles.statusHeadingText}
                                            />
                                        )}
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataTableCell}>
                                        {getInvoiceValueForPackage(
                                            deliveryId,
                                            order.properties?.deliveryInvoiceNumbers,
                                        ) && (
                                            <SmallHeadingAndTextOrderLineDelivery
                                                heading="INVOICE#"
                                                text={getInvoiceValueForPackage(
                                                    deliveryId,
                                                    order.properties?.deliveryInvoiceNumbers,
                                                )}
                                                extendedStyles={styles.invoiceNumberHeadingText}
                                            />
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            </DataTableBody>
                        </DataTable>
                    </Hidden>
                    <Hidden
                        above="sm"
                        css={css`
                            width: 100%;
                        `}
                    >
                        <StyledWrapper
                            css={css`
                                width: 100%;
                                display: flex;
                                @media print {
                                    background: ${getColor("common.accent")} !important;
                                    width: max-content;
                                }
                                background: ${getColor("common.accent")} !important;
                            `}
                        >
                            <StyledWrapper {...styles.mobileHeaderFirstrow}>
                                {(orderLines.orderLine[0].properties?.shippingMethod ||
                                    order.shipViaDescription ||
                                    order.shipCode) && (
                                    <SmallHeadingAndTextOrderLineDelivery
                                        heading={`DELIVERY ${
                                            Object.entries(groupedOrderLines).length > 1
                                                ? `${index + 1}/${Object.entries(groupedOrderLines).length}`
                                                : ""
                                        }`}
                                        text={orderLines.carrier || ""}
                                        extendedStyles={styles.orderNumberHeadingAndText}
                                    />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.mobileHeaderFirstrow}>
                                {(orderLines.realtimeOrderStatusdeliveryId?.status ||
                                    shipmentStatusByDeliveryId[deliveryId] ||
                                    order.status) && (
                                    <StatusLableAndHeading
                                        heading={translate("Status")}
                                        text={getOrderStatus(orderLines, deliveryId)}
                                        extendedStyles={styles.orderNumberHeadingAndText}
                                    />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.mobileHeaderSecondtrow}>
                                {getInvoiceValueForPackage(deliveryId, order.properties?.deliveryInvoiceNumbers) && (
                                    <SmallHeadingAndTextOrderLineDelivery
                                        heading="Invoice#"
                                        text={getInvoiceValueForPackage(
                                            deliveryId,
                                            order.properties?.deliveryInvoiceNumbers,
                                        )}
                                        extendedStyles={styles.invoiceNumberHeadingText}
                                    />
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </Hidden>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsInvoiceTable),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
